import React, { useMemo } from 'react';
import { capitalize, flatten, toLower } from 'lodash';
import { Text, View } from '@react-pdf/renderer';
import PdfSection from 'Views/Pages/Pdf/Components/PDFSection';
import {
	CourseEntity, PlanSelectionEntity, ProgramRuleCourse, ProgramRuleEntity,
} from 'Models/Entities';
import {
	buildRequirementTree,
	checkRequirementRulesViolation,
	RequirementNode,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { compareTwoDate } from 'Util/DateTimeUtils';
import PdfCourseAccordion from 'Views/Pages/Pdf/Components/PDFCourseAccordion';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import PdfCourseAccordionLabel from 'Views/Pages/Pdf/Components/PdfCourseAccordionLabel';
import PdfCourseSelectionNotice from 'Views/Pages/Pdf/Components/PDFCourseSelectionNotice';
import PdfCourseAccordionItem from 'Views/Pages/Pdf/Components/PDFCourseAccordionItem';
import PdfChipGroup from 'Views/Pages/Pdf/Chip/PDFChipGroup';
import PdfChip from 'Views/Pages/Pdf/Chip/PdfChip';
import { DataIndex, indexByProperty, indexByPropertyArray } from 'Util/DataIndexUtils';

interface ProgramRulesProps {
	programRules: ProgramRuleEntity[],
	planSelections: PlanSelectionEntity[],
	coursesById: DataIndex<CourseEntity>,
	completedCourseIds: string[],
	description?: string,
	getCourseComponents: (courseId: string) => string[];
}

const ProgramRules = (props: ProgramRulesProps) => {
	const {
		programRules,
		planSelections,
		coursesById,
		completedCourseIds,
		description,
		getCourseComponents,
	} = props;

	const rootProgramRules = useMemo(() => {
		if (!programRules) {
			return [] as ProgramRuleEntity[];
		}

		return programRules.filter(programRule => !programRule.parentProgramRuleId);
	}, [programRules]);

	const programRulesByParentId: DataIndex<ProgramRuleEntity[]> = useMemo(() => {
		if (!programRules) {
			return {} as DataIndex<ProgramRuleEntity[]>;
		}

		return indexByPropertyArray(
			programRules,
			programRule => programRule.parentProgramRuleId || 'requirements',
		);
	}, [programRules]);

	const programRuleCourseById = useMemo((): DataIndex<ProgramRuleCourse> => {
		if (!programRules) {
			return {} as DataIndex<ProgramRuleCourse>;
		}

		return indexByProperty(
			flatten(programRules.map(programRule => programRule.courses)),
			'id',
		);
	}, [programRules]);

	// Temp plan selections of the plan's selections that are mapped to program rule courses
	const mappedPlanSelections = useMemo(() => {
		const programRuleCourses = Object.values(programRuleCourseById);

		if (programRuleCourses.length === 0) {
			return [] as PlanSelectionEntity[];
		}

		return programRuleCourses.filter(offering => {
			const { courseId } = offering;

			return !!planSelections.find(selection => selection.courseId === courseId);
		}).map(programRuleCourse => new PlanSelectionEntity({
			courseId: programRuleCourse.courseId,
			locationId: programRuleCourse.programRuleId,
		}));
	}, [planSelections, programRuleCourseById]);

	const offeringIdsByCourseIdByRequirementId = useMemo(() => {
		const _offeringsByCourseIdByRequirementId: DataIndex<DataIndex<string[]>> = {};

		for (const { id, locationId, courseId } of Object.values(mappedPlanSelections)) {
			const existingCourses = _offeringsByCourseIdByRequirementId[locationId];
			if (!existingCourses) {
				_offeringsByCourseIdByRequirementId[locationId] = {};
			}

			const existingCourseOfferings = _offeringsByCourseIdByRequirementId[locationId][courseId];
			if (!existingCourseOfferings) {
				_offeringsByCourseIdByRequirementId[locationId][courseId] = [];
			}

			_offeringsByCourseIdByRequirementId[locationId][courseId].push(id);
		}

		return _offeringsByCourseIdByRequirementId;
	}, [mappedPlanSelections]);

	const requirementTree = useMemo(() => {
		if (!rootProgramRules.length && mappedPlanSelections.length === 0) {
			return {};
		}

		return buildRequirementTree(
			offeringIdsByCourseIdByRequirementId,
			rootProgramRules,
			programRulesByParentId,
			mappedPlanSelections,
			coursesById,
			programRuleCourseById,
		);
	}, [
		rootProgramRules,
		mappedPlanSelections,
		offeringIdsByCourseIdByRequirementId,
		coursesById,
		programRulesByParentId,
		programRuleCourseById,
	]);

	const renderRequirementTree = (requirementTreeNode: RequirementNode, depth: number) => {
		const { requirement } = requirementTreeNode;

		if (requirement.category === 'REQUIREMENTS') {
			return renderRequirementAccordion(requirementTreeNode, depth);
		}

		return renderCourseAccordion(requirementTreeNode, depth);
	};

	const renderRequirementAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		const childNodes = requirementNode.childNodes
			.sort(
				(a, b) => compareTwoDate(
					new Date(a.requirement.created),
					new Date(b.requirement.created),
				),
			);

		return (
			<PdfCourseAccordion<RequirementNode>
				key={requirement.id}
				title={requirement.displayName}
				depth={depth}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={childNodes}
				renderItem={(item, index) => {
					return renderRequirementTree(item, depth + 1);
				}}
				emptyLabel={`No ${window.lexicon.courses}`}
				description={(
					<Conditional
						condition={requirement.description}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<Text style={{ fontStyle: 'italic' }}>{requirement.description}</Text>
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<PdfCourseSelectionNotice />
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
			/>
		);
	};

	const renderCourseAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const courses = Object.keys(requirementNode.offeringIdsByCourseId)
			.map(courseId => coursesById[courseId])
			.sort((a, b) => a.code.localeCompare(b.code));

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		return (
			<PdfCourseAccordion<CourseEntity>
				key={requirement.id}
				title={requirement.displayName}
				depth={depth}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={courses}
				renderItem={(course, index) => {
					const courseComponents = getCourseComponents(course.id);

					return (
						<PdfCourseAccordionItem
							key={course.id}
							course={course}
							label={(
								<PdfChipGroup>{
									courseComponents.map(component => (
										<PdfChip label={component} />
									))
								}
								</PdfChipGroup>
							)}
							additionalStatus={
								completedCourseIds.includes(course.id)
									? (
										<PdfChipGroup>
											<PdfChip label={'Completed/Credit'} isSuccess />
										</PdfChipGroup>
									)
									: null
							}
						/>
					);
				}}
				emptyLabel={`No ${window.lexicon.courses}`}
				description={(
					<Conditional
						condition={requirement.description}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<Text style={{ fontStyle: 'italic' }}>{requirement.description}</Text>
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={PdfCourseAccordionLabel}
						componentProps={{
							content: (
								<PdfCourseSelectionNotice />
							),
							isError: hasViolatedRequirementRules,
						}}
					/>
				)}
			/>
		);
	};

	return (
		<PdfSection
			title={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.rules)}`}
			description={description}
			showDivider={false}
		>
			<View style={{ display: 'flex', gap: '8' }}>
				{
					rootProgramRules.length > 0
					&& requirementTree[rootProgramRules[0].id].childNodes
						.map(requirementNode => renderRequirementTree(requirementNode, 0))
				}
			</View>
		</PdfSection>
	);
};

export default ProgramRules;
